import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FiCopy, FiCheck, FiSearch, FiArrowUpRight } from "react-icons/fi";
import { Helmet } from "react-helmet";
import { REACT_APP_API_PORT } from "../Api";

const FREE_COMMAND_LIMIT = 20;
const UNLOCK_CACHE_KEY = "claude_command_user";

/*
  Claude AI Prompt Shortcuts Library
  - 50 community-style prompt shortcuts
  - Search + one-click copy
  - Reference-inspired editorial design
  - Two extra power-user sections
  - Important: not every slash shortcut below is a native Claude command.
*/

const LEARN_URL = "https://learn.nextfutureai.in/web/checkout/6a080257fef35bfdff106b9c";

const COMMANDS = [
  { id: 1, number: 1, command: "/ghost", title: "Privacy-focused prompt cue", description: "Use a privacy-focused workflow for sensitive work. Note: this is a prompt convention, not a universal built-in command." },
  { id: 2, number: 2, command: "/council", title: "COUNCIL MODE", description: "Ask for multiple independent perspectives, then compare the viewpoints before choosing a direction." },
  { id: 3, number: 3, command: "/research", title: "RESEARCH MODE", description: "Use web research when available and synthesize current information with clear source context." },
  { id: 4, number: 4, command: "/focus", title: "FOCUS MODE", description: "Keep the task centered on the most relevant context and avoid unnecessary digressions." },
  { id: 5, number: 5, command: "/brief", title: "BRIEF MODE", description: "Give a shorter, punchier answer with only the information needed to complete the request." },
  { id: 6, number: 6, command: "/code", title: "CODE MODE", description: "Prioritize implementation-ready code, correctness, and concise technical explanation." },
  { id: 7, number: 7, command: "/creative", title: "CREATIVE MODE", description: "Explore more imaginative ideas, variations, storytelling, and creative directions." },
  { id: 8, number: 8, command: "/deep", title: "DEEP ANALYSIS MODE", description: "Spend more effort comparing assumptions, trade-offs, edge cases, and complex reasoning." },
  { id: 9, number: 9, command: "/nocite", title: "NO CITATIONS MODE", description: "When citations are not needed, keep the response focused on the requested content without citation formatting." },
  { id: 10, number: 10, command: "/format:json", title: "SPECIFY OUTPUT FORMAT", description: "Return the answer as valid JSON. Other useful variants include /format:markdown and /format:csv." },
  { id: 11, number: 11, command: "/length:short", title: "SET RESPONSE LENGTH", description: "Ask for a short response. You can adapt the same pattern with /length:medium or /length:long." },
  { id: 12, number: 12, command: "/audience:tech", title: "TARGET AUDIENCE SETTING", description: "Set the audience level, such as technical, executive, or beginner, so the explanation matches the reader." },
  { id: 13, number: 13, command: "/cache", title: "REUSE PREVIOUS CONTEXT", description: "Reuse relevant information already available in the conversation instead of asking for the same details again." },
  { id: 14, number: 14, command: "/batch:10", title: "BATCH MODE", description: "Process multiple items together in one structured response. Replace 10 with the number of items you need." },
  { id: 15, number: 15, command: "/summary", title: "AUTO-SUMMARY MODE", description: "Summarize the main response into a compact recap, especially useful for long outputs." },
  { id: 16, number: 16, command: "/template", title: "USE TEMPLATE MODE", description: "Fill a reusable response structure such as JSON, markdown, a report, or another template you provide." },
  { id: 17, number: 17, command: "/noexplain", title: "SKIP EXTRA EXPLANATIONS", description: "Return the requested output with minimal explanation when you already understand the reasoning." },
  { id: 18, number: 18, command: "/stream", title: "STREAMING MODE", description: "Prefer an immediate streaming-style response when the interface supports it." },
  { id: 19, number: 19, command: "/compare:A,B,C", title: "STRUCTURED COMPARISON", description: "Compare A, B, and C using the same criteria and present the differences in a clear structure." },
  { id: 20, number: 20, command: "/list:bullets", title: "OPTIMIZE LIST OUTPUT", description: "Return information as bullet points. You can also request numbered or prose lists." },
  { id: 21, number: 21, command: "/think", title: "REASONING MODE", description: "Ask for a careful reasoning process or concise rationale. The interface may not expose private chain-of-thought." },
  { id: 22, number: 22, command: "/quiet", title: "QUIET MODE", description: "Give only the essential answer with minimal commentary, formatting, and follow-up suggestions." },
  { id: 23, number: 23, command: "/context:5", title: "LIMIT WORKING CONTEXT", description: "Tell Claude to prioritize the last five relevant messages or the latest provided context." },
  { id: 24, number: 24, command: "/pro", title: "PRO WORKFLOW", description: "Use a more deliberate, higher-effort workflow when your Claude plan or model supports the needed capability." },
  { id: 25, number: 25, command: "/beta", title: "BETA FEATURES", description: "Use experimental features only when they are actually enabled in your Claude environment." },
  { id: 26, number: 26, command: "/clear", title: "CLEAR CONVERSATION", description: "Start fresh when your environment supports the command; Claude Code officially supports /clear." },
  { id: 27, number: 27, command: "/revert", title: "REVERT / ROLLBACK", description: "Return to an earlier state when your Claude environment provides a rollback or checkpoint feature." },
  { id: 28, number: 28, command: "/edit:text", title: "EDIT LAST RESPONSE", description: "Ask Claude to revise only the specified text or section instead of recreating the whole answer." },
  { id: 29, number: 29, command: "/branch", title: "CREATE CONVERSATION BRANCH", description: "Explore an alternative approach while keeping the original direction available when your interface supports branching." },
  { id: 30, number: 30, command: "/export:md", title: "EXPORT AS MARKDOWN", description: "Prepare the conversation or result in Markdown format for easy saving or sharing." },
  { id: 31, number: 31, command: "/model:haiku", title: "SWITCH MODEL", description: "Request a faster/lighter model when your Claude environment supports model selection; model availability varies." },
  { id: 32, number: 32, command: "/quality:draft", title: "SET RESPONSE QUALITY", description: "Request a quick draft when speed matters, or use a stronger quality instruction when polish matters more." },
  { id: 33, number: 33, command: "/custom:formal", title: "CUSTOM BEHAVIOR RULE", description: "Apply a reusable behavior rule such as formal tone, no-markdown formatting, or a specific output convention." },
  { id: 34, number: 34, command: "/memory", title: "MEMORY / PROJECT CONTEXT", description: "Use saved project context when available. In Claude Code, /memory is an official command for viewing or editing CLAUDE.md files." },
  { id: 35, number: 35, command: "/share", title: "SHARE CONVERSATION", description: "Prepare a shareable version of the conversation when sharing is available in your Claude product." },
  { id: 36, number: 36, command: "/version:2024", title: "KNOWLEDGE CUTOFF CONTEXT", description: "Frame the answer around a specified historical date when the task requires historical consistency." },
  { id: 37, number: 37, command: "/timeout:10", title: "TIME-BOX THE TASK", description: "Ask Claude to prioritize the most useful result within a defined time constraint; exact timeout controls depend on the environment." },
  { id: 38, number: 38, command: "/confidence", title: "CONFIDENCE CHECK", description: "State confidence or uncertainty for important claims and flag where verification is recommended." },
  { id: 39, number: 39, command: "/cite:apa", title: "CITATION FORMAT CONTROL", description: "Use APA-style citations when sources are required. You can adapt this to Chicago, MLA, or another style." },
  { id: 40, number: 40, command: "/voice:casual", title: "VOICE / TONE CONTROL", description: "Use a casual, natural voice. Variants can request professional, concise, humorous, or other tones." },
  { id: 41, number: 41, command: "/private", title: "PRIVATE WORKFLOW", description: "Use privacy-conscious handling and avoid unnecessary retention or sharing. Exact privacy behavior depends on the Claude product and settings." },
  { id: 42, number: 42, command: "/limit:500", title: "SET RESPONSE LIMIT", description: "Ask Claude to keep the response under roughly 500 tokens or words as specified. Treat it as a prompt constraint unless your tool supports hard limits." },
  { id: 43, number: 43, command: "/follow-up:off", title: "DISABLE FOLLOW-UP QUESTIONS", description: "Ask for a self-contained response without optional follow-up questions or extra prompts." },
  { id: 44, number: 44, command: "/examples:off", title: "SKIP EXAMPLES", description: "Return the core answer without examples when you already understand the concept." },
  { id: 45, number: 45, command: "/math:latex", title: "MATH NOTATION CONTROL", description: "Format mathematical expressions in LaTeX. You can request Unicode or plain-text notation instead." },
  { id: 46, number: 46, command: "/coding:python", title: "LOCK CODING LANGUAGE", description: "Write code specifically in Python. Replace Python with JavaScript, TypeScript, SQL, or another language." },
  { id: 47, number: 47, command: "/api-ready", title: "API-READY OUTPUT", description: "Return structured, machine-readable output suitable for API integration, with no unnecessary prose." },
  { id: 48, number: 48, command: "/debug", title: "DEBUG MODE", description: "Inspect the task for errors, assumptions, edge cases, and likely failure points before proposing a fix." },
  { id: 49, number: 49, command: "/archive", title: "ARCHIVE WORKFLOW", description: "Move completed or low-priority material out of the active workflow when your Claude product supports archiving." },
  { id: 50, number: 50, command: "/smart-cache", title: "SMART REUSE WORKFLOW", description: "Reuse stable context, templates, or previously established patterns instead of repeating the same setup." },
];

const CATEGORY_GROUPS = [
  { name: "Core Modes", range: [0, 12] },
  { name: "Output & Context", range: [12, 25] },
  { name: "Workflow & Editing", range: [25, 40] },
  { name: "Developer & Utility", range: [40, 50] },
];

const comboSections = [
  [
    "Maximum Token Savings",
    [
      ["Quick Answers", "60–70% less output", "/brief", "/noexplain", "/limit:300"],
      ["Coding Tasks", "40–50% less overhead", "/code", "/noexplain", "/format:json", "/coding:python"],
      ["Private Batch Work", "45–55% less repetition", "/ghost", "/batch:5", "/format:json", "/quiet"],
      ["Research", "20–30% less rework", "/research", "/format:json", "/nocite", "/length:medium"]
    ]
  ],
  [
    "Power User Secrets",
    [
      [
        "/smart-cache is a powerful reuse pattern",
        "Reuse stable context, templates, and repeated structures instead of rebuilding the same setup every time."
      ],
      [
        "/debug makes optimization easier",
        "Use it to inspect failure points, assumptions, edge cases, and the quality of the current output."
      ],
      [
        "Choose the lightest model that fits",
        "Use a faster model for simple tasks and reserve stronger models for complex reasoning when your plan supports model switching."
      ],
      [
        "Never ask for the same setup twice",
        "Keep reusable instructions in a template, project file, skill, or saved context when your Claude environment supports it."
      ],
      [
        "Batch + structured output",
        "Combine batching with a predictable format when processing many items to make the result easier to review or parse."
      ]
    ]
  ]
];
export default function ClaudeGuide() {
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState("All");
  const [copied, setCopied] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", mobile: "" });
  const [leadError, setLeadError] = useState("");
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isSavingMobile, setIsSavingMobile] = useState(false);
  const unlockTriggerRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return COMMANDS.filter((item, index) => {
      const group = CATEGORY_GROUPS.find(g => index >= g.range[0] && index < g.range[1])?.name;
      const groupMatch = activeGroup === "All" || group === activeGroup;
      const searchMatch = !q || `${item.command} ${item.title} ${item.description} ${group}`.toLowerCase().includes(q);
      return groupMatch && searchMatch;
    });
  }, [search, activeGroup]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(UNLOCK_CACHE_KEY) || sessionStorage.getItem(UNLOCK_CACHE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.name && parsed?.email && parsed?.mobile) {
          setIsUnlocked(true);
          setLeadForm(parsed);
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (isUnlocked || search.trim() || activeGroup !== "All") return;
    try {
      if (sessionStorage.getItem("claude_command_popup_shown") === "1") return;
    } catch {}

    const target = unlockTriggerRef.current;
    if (!target || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      setShowUnlockModal(true);
      try { sessionStorage.setItem("claude_command_popup_shown", "1"); } catch {}
      observer.disconnect();
    }, { root: null, rootMargin: "0px 0px 120px 0px", threshold: 0.01 });

    observer.observe(target);
    return () => observer.disconnect();
  }, [isUnlocked, search, activeGroup]);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    const name = leadForm.name.trim();
    const email = leadForm.email.trim().toLowerCase();
    const mobile = leadForm.mobile.trim();

    if (!name) return setLeadError("Please enter your name");
    if (!email) return setLeadError("Please enter your email");
    if (!mobile) return setLeadError("Please enter your mobile number");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setLeadError("Please enter a valid email.");
    }
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      return setLeadError("Please enter a valid 10-digit mobile number.");
    }

    try {
      setIsSavingMobile(true);
      setLeadError("");

      const res = await axios.post(
      `${REACT_APP_API_PORT}user/homepage/guide_users_claude`,
      {
        name,
        email,
        mobile,
      }
    );

      if (!res.data?.status) {
        setLeadError(res.data?.message || "Something went wrong. Please try again.");
        return;
      }

      const userDetails = { name, email, mobile };
      try {
        localStorage.setItem(UNLOCK_CACHE_KEY, JSON.stringify(userDetails));
        sessionStorage.setItem(UNLOCK_CACHE_KEY, JSON.stringify(userDetails));
      } catch {}

      setLeadForm(userDetails);
      setIsUnlocked(true);
      setShowUnlockModal(false);
      setLeadError("");
    } catch (error) {
      console.error("Unlock API Error:", error);
      setLeadError(
        error?.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setIsSavingMobile(false);
    }
  };

  const copy = async (item) => {
    if (!isUnlocked && item.number > FREE_COMMAND_LIMIT) {
      setShowUnlockModal(true);
      return;
    }

    const text = `${item.command}\n${item.description}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(item.command);
      setTimeout(() => setCopied(""), 1400);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#000000] text-[#FFFFFF]">
      <Helmet>
        <title>50 Claude AI Prompt Shortcuts | Claude Guide</title>
        <meta name="description" content="Explore 50 Claude AI prompt shortcuts for research, coding, writing, output control, privacy workflows and productivity." />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,600;0,700;1,600;1,700&display=swap');
        .cmd-serif { font-family:'Playfair Display', Georgia, serif; }
        .cmd-sans { font-family:'DM Sans', Arial, sans-serif; }
        html, body, #root { min-height:100%; margin:0; }
        body { background:#000000; color:#FFFFFF; }
        .claude-bg { min-height:100vh; overflow-x:hidden; overflow-y:visible; background:linear-gradient(180deg,#000000 0%,#000000 100%); }
        .shortcut-card { box-shadow:0 10px 28px rgba(0,0,0,.35); }
        .shortcut-card:hover { box-shadow:0 16px 34px rgba(0,0,0,.55); transform:translateY(-2px); }
        .learn-strip:hover { border-color:#111111; background:rgba(255,255,255,.08); }
        .scrollbar-hide { scrollbar-width:none; -ms-overflow-style:none; }
        .scrollbar-hide::-webkit-scrollbar { display:none; }
      `}</style>

      <main className="cmd-sans claude-bg min-h-screen">
        <section className="mx-auto max-w-[1260px] px-5 pb-10 pt-9 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-[980px] text-center">
            <div className="inline-flex items-center rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-5 py-2 text-[10px] font-bold uppercase tracking-[.18em] text-white shadow-[0_8px_22px_rgba(255,255,255,.10)]">
              ✦ Claude AI Prompt Library
            </div>
            <h1 className="cmd-serif mt-6 text-[40px] font-bold leading-[1.05] tracking-[-.035em] text-[#FFFFFF] sm:text-[58px]">
              50 Claude AI <span className="block italic text-[#FFFFFF]">Prompt Shortcuts</span>
            </h1>
            <div className="mx-auto mt-4 h-[3px] w-14 rounded-full bg-[#111111]" />
            <p className="mx-auto mt-6 max-w-[790px] text-[14px] leading-6 text-[#AAAAAA] sm:text-[16px]">
              A visual shortcut library for faster research, coding, writing, output control and everyday Claude workflows.
            </p>
            <div className="mt-5 text-[11px] leading-5 text-[#999999]">
              These are <strong>prompt-style shortcuts</strong>; some are community conventions rather than native Claude commands.
            </div>
            <div className="mt-7 flex flex-wrap justify-center gap-3 text-[10px] font-bold uppercase tracking-[.12em] text-[#111]">
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">⚡ 50 Shortcuts</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">🧠 Thinking + Output</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">💻 Coding + Research</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">📋 One-Click Copy</span>
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-[1040px] rounded-[26px] border border-[#2A2A2A] bg-[#0A0A0A]/80 p-5 shadow-[0_18px_55px_rgba(0,0,0,.45)] sm:p-7">
            <h2 className="cmd-serif text-[24px] font-bold text-[#FFFFFF]">🎯 How to use these shortcuts</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                ["STEP 1", "Pick a shortcut", "Choose the mode or instruction that matches your task."],
                ["STEP 2", "Add your request", "Write your actual task after the shortcut."],
                ["STEP 3", "Copy & use", "Copy the shortcut and paste it into Claude."],
              ].map(([a,b,c]) => (
                <div key={a} className="rounded-[18px] border border-[#2A2A2A] bg-white/5 p-5">
                  <div className="text-[10px] font-bold tracking-[.15em] text-[#FFFFFF]">{a}</div>
                  <div className="mt-2 text-[15px] font-bold text-[#EEEEEE]">{b}</div>
                  <p className="mt-1 text-[12px] leading-5 text-[#AAAAAA]">{c}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1260px] px-5 pb-16 sm:px-8 lg:px-10">
          <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em] text-white">ALL 50 SHORTCUTS</div>
              <h2 className="cmd-serif mt-4 text-[34px] font-bold tracking-[-.03em] text-[#FFFFFF] sm:text-[46px]">The Complete Shortcut Library</h2>
              <p className="mt-2 text-sm text-[#AAAAAA]">Search, filter and copy any Claude shortcut in seconds.</p>
            </div>
            <div className="w-full lg:w-[370px]">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999]" />
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search shortcuts, modes or uses..." className="h-12 w-full rounded-2xl border border-[#2A2A2A] bg-white/5 pl-11 pr-4 text-sm text-[#FFFFFF] outline-none transition focus:border-[#111111] focus:ring-4 focus:ring-[#111111]/10" />
              </div>
            </div>
          </div>

          <div className="mb-8 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button onClick={()=>setActiveGroup("All")} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition ${activeGroup === "All" ? "bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white" : "border border-[#2A2A2A] bg-white/5 text-[#fff] hover:border-[#555555]"}`}>All · 50</button>
            {CATEGORY_GROUPS.map(group => (
              <button key={group.name} onClick={()=>setActiveGroup(group.name)} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition ${activeGroup === group.name ? "bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white" : "border border-[#2A2A2A] bg-white/5 text-[#fff] hover:border-[#555555]"}`}>{group.name}</button>
            ))}
          </div>

          <div className="mb-5 text-sm font-semibold text-[#AAAAAA]">Showing <strong>{filtered.length}</strong> shortcut{filtered.length === 1 ? "" : "s"}</div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
  {filtered.map((item, idx) => (
    <React.Fragment key={item.id}>

      <article
        className={`shortcut-card group relative rounded-[20px] border p-5 transition duration-200 ${
          !isUnlocked && item.number > FREE_COMMAND_LIMIT
            ? "border-[#2A2A2A] bg-[#111111]"
            : "border-[#E8E8E8] bg-[#FFFFFF] hover:-translate-y-0.5"
        }`}
      >
        {!isUnlocked && item.number > FREE_COMMAND_LIMIT && (
          <div
            className="absolute inset-0 z-20 flex cursor-pointer flex-col items-center justify-center rounded-[20px] bg-[#111111]/90 px-5 text-center backdrop-blur-[3px]"
            onClick={() => setShowUnlockModal(true)}
          >
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-[#444444] bg-[#222222] text-lg">
              🔒
            </div>
            <p className="text-sm font-bold text-white">This Shortcut is Locked</p>
            <p className="mt-1 text-[11px] leading-4 text-[#999999]">
              Submit your details to unlock all shortcuts
            </p>
          </div>
        )}

        {/* Pink left accent */}
        <div className="absolute left-0 top-5 h-10 w-[3px] rounded-r-full bg-[#FF5F87]" />

        <div className="flex items-start justify-between gap-3 pl-2">

          <div className="min-w-0 flex-1">

            <div className="flex items-center justify-between gap-2">

              {/* Command */}
              <code className="rounded-lg bg-[#FFF1F4] px-2.5 py-1 text-[15px] font-bold tracking-[-.02em] text-[#111111]">
                {item.command}
              </code>

              {/* Number */}
              <span className="shrink-0 rounded-full bg-[#111111] px-2 py-1 text-[8px] font-bold uppercase tracking-[.05em] text-white">
                #{String(item.id).padStart(3, "0")}
              </span>

            </div>

            {/* Category */}
            <div className="mt-4 text-[10px] font-bold uppercase tracking-[.12em] text-[#111111]">
              {item.title}
            </div>

            {/* Description */}
            <p className="mt-2 text-[13px] leading-5 text-[#6B4A55]">
              {item.description}
            </p>

          </div>

          {/* Copy Button */}
          <button
            onClick={() => copy(item)}
            aria-label={`Copy ${item.command}`}
            className={`shrink-0 rounded-xl p-2.5 transition ${
              copied === item.command
                ? "bg-[#FF5F87] text-white"
                : "bg-[#FFF1F4] text-[#9A6073] hover:bg-[#FF5F87] hover:text-white"
            }`}
          >
            {copied === item.command ? <FiCheck /> : <FiCopy />}
          </button>

        </div>

        {/* Bottom CTA */}
        <div className="mt-4 border-t border-[#E8DDE1] pt-3 text-[10px] font-semibold uppercase tracking-[.10em] text-[#9A7A84]">
          Type{" "}
          <span className="text-[#FF5F87]">
            {item.command}
          </span>{" "}
          + your request

          <a
            href={LEARN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="float-right font-bold text-[#FF5F87] hover:underline"
          >
            LEARN HOW →
          </a>
        </div>

      </article>

      {item.number === FREE_COMMAND_LIMIT &&
        !isUnlocked &&
        !search.trim() &&
        activeGroup === "All" && (
          <div ref={unlockTriggerRef} className="col-span-full h-2" aria-hidden="true" />
        )}

      {/* Learn More Strip */}
      {(idx + 1) % 12 === 0 && idx !== filtered.length - 1 && (
        <a
          href={LEARN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="learn-strip md:col-span-2 xl:col-span-3 rounded-[18px] border border-[#2A2A2A] bg-[#181818] px-5 py-3.5 transition hover:bg-[#202020]"
        >
          <div className="flex items-center justify-between gap-4">

            <div>
              <div className="text-[9px] font-bold uppercase tracking-[.16em] text-[#FF5F87]">
                Keep learning
              </div>

              <div className="mt-1 text-[12px] font-semibold text-[#CCCCCC]">
                Explore practical AI workflows and prompting techniques.
              </div>
            </div>

            <span className="shrink-0 text-[10px] font-bold uppercase tracking-[.12em] text-[#FF5F87]">
              Learn more →
            </span>

          </div>
        </a>
      )}

    </React.Fragment>
  ))}
</div>

          {filtered.length === 0 && (
            <div className="rounded-[24px] border border-[#2A2A2A] bg-white/5 p-14 text-center">
              <div className="text-4xl">🔎</div>
              <h3 className="cmd-serif mt-4 text-2xl font-bold text-[#FFFFFF]">No shortcuts found</h3>
              <p className="mt-2 text-sm text-[#AAAAAA]">Try another keyword or mode.</p>
            </div>
          )}
        </section>

       <section className="mx-auto max-w-[1140px] px-5 pb-14 sm:px-8">
  <div className="mb-4 inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em] ">
    ⚡ PRO COMBINATIONS
  </div>

  <h2 className="cmd-serif text-center text-[32px] font-bold text-[#FFFFFF] sm:text-[40px]">
    Maximum Token Savings
  </h2>

  <div className="mx-auto mt-3 h-[2px] w-14 rounded-full bg-[#FF5F87]" />

  <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
    {(Array.isArray(comboSections[0]?.[1]) ? comboSections[0][1] : []).map(
      ([name, saving, ...tags], i) => (
        <div
          key={name}
          className="group rounded-[20px] border border-[#2F2F2F] bg-[#0D0D0D] p-6 text-center transition duration-200 hover:border-[#555555] hover:bg-[#111111]"
        >
          {/* Combination number */}
         <div className="flex flex-col items-center">
  <div className="text-[12px] font-bold uppercase tracking-[.16em] text-[#fff]">
    COMBINATION #{i + 1}
  </div>

  <div className="mt-2 h-[2px] w-10 rounded-full bg-[#FF5F87]" />
</div>

          {/* Title */}
          <div className="cmd-serif mt-2 text-[18px] font-bold text-[#FFFFFF]">
            {name}
          </div>

          {/* Saving */}
          <div className="mt-2 text-[18px] font-semibold leading-8 text-[#FFFFFF]">
            {saving}
          </div>

          {/* Command tags */}
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {tags.map((t) => (
              <span
                key={t}
                className="rounded-md border border-[#292929] bg-[#181818] px-2.5 py-1 text-[9px] font-semibold text-[#CCCCCC] transition group-hover:border-[#3A3A3A] group-hover:text-[#FFFFFF]"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )
    )}
  </div>
</section>

       <section className="mx-auto max-w-[1140px] px-5 pb-20 sm:px-8">

  {/* Section Label */}
  <div className="mb-4 inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em] text-white">
    💡 TOP 5 PRO TIPS
  </div>

  {/* Heading */}
  <h2 className="cmd-serif text-center text-[32px] font-bold text-[#FFFFFF] sm:text-[40px]">
    Power User Secrets
  </h2>

  {/* Underline */}
  <div className="mx-auto mt-3 h-[2px] w-12 rounded-full bg-[#FF5F87]" />

  {/* Cards */}
  <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">

    {(Array.isArray(comboSections[1]?.[1])
      ? comboSections[1][1]
      : []
    ).map(([title, body], i) => (

      <div
        key={title}
        className="group relative rounded-[20px] border border-[#2A2A2A] bg-[#0D0D0D] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-[#444444] hover:bg-[#111111]"
      >

        <div className="flex items-start gap-4">

          {/* Number */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#2A2A2A] bg-[#181818] text-[12px] font-bold text-[#FFFFFF] transition group-hover:border-[#FF5F87] group-hover:text-[#FF5F87]">
            {i + 1}
          </div>

          <div className="min-w-0 flex-1">

            {/* Title */}
            <div className="text-[13px] font-bold leading-5 text-[#FFFFFF]">
              {title}
            </div>

         
            {/* Description */}
            <p className="mt-2 text-[12px] leading-5 text-[#999999]">
              {body}
            </p>

          </div>

        </div>

      </div>

    ))}

  </div>
</section>

        {showUnlockModal && !isUnlocked && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm"
            onClick={() => setShowUnlockModal(false)}
          >
            <div
              className="relative max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-[24px] border border-[#333333] bg-[#181818] p-5 shadow-[0_20px_70px_rgba(0,0,0,.55)] sm:p-7"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" onClick={() => setShowUnlockModal(false)}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-[#242424] text-lg text-[#999999] hover:bg-[#333333] hover:text-white">
                ×
              </button>
              <div className="pr-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-xl">
                  🔓
                </div>
                <h3 className="cmd-serif mt-4 text-2xl font-bold text-white">
                  Unlock All 50 Shortcuts
                </h3>
                <p className="mt-2 text-xs leading-5 text-[#999999]">
                  The first 20 shortcuts are free.<br />
                  Enter your details to unlock the complete library.
                </p>
              </div>
              <form onSubmit={handleLeadSubmit} className="mt-6 space-y-3">
                <input type="text" value={leadForm.name}
                  onChange={(e) => { setLeadForm({ ...leadForm, name: e.target.value }); setLeadError(""); }}
                  placeholder="Enter your name" autoComplete="name"
                  className="h-12 w-full rounded-xl border border-[#333333] bg-[#111111] px-4 text-sm text-white outline-none placeholder:text-[#666666] focus:border-[#e1307a]" />
                <input type="email" value={leadForm.email}
                  onChange={(e) => { setLeadForm({ ...leadForm, email: e.target.value }); setLeadError(""); }}
                  placeholder="Enter your email" autoComplete="email"
                  className="h-12 w-full rounded-xl border border-[#333333] bg-[#111111] px-4 text-sm text-white outline-none placeholder:text-[#666666] focus:border-[#e1307a]" />
                <input type="tel" inputMode="numeric" maxLength={10} value={leadForm.mobile}
                  onChange={(e) => { setLeadForm({ ...leadForm, mobile: e.target.value.replace(/\D/g, "") }); setLeadError(""); }}
                  placeholder="Enter mobile number" autoComplete="tel"
                  className="h-12 w-full rounded-xl border border-[#333333] bg-[#111111] px-4 text-sm text-white outline-none placeholder:text-[#666666] focus:border-[#e1307a]" />
                {leadError && <p className="text-center text-xs text-red-400">{leadError}</p>}
                <button type="submit" disabled={isSavingMobile}
                  className="h-12 w-full rounded-xl bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-sm font-bold text-white disabled:opacity-60">
                  {isSavingMobile ? "Unlocking..." : "Unlock All Shortcuts →"}
                </button>
                <p className="text-center text-[9px] text-[#666666]">
                  Your email will be used for future AI updates & resources.
                </p>
              </form>
            </div>
          </div>
        )}

        <footer className="border-t border-[#2A2A2A] bg-[#080808] py-10 text-center">
          <div className="cmd-serif text-[22px] font-bold text-[#FFFFFF]">50 Claude AI Prompt Shortcuts</div>
          <p className="mx-auto mt-2 max-w-[700px] px-5 text-xs leading-5 text-[#AAAAAA]">Use these as reusable prompt cues. Native Claude/Claude Code availability varies by product, plan, plugins and your setup.</p>
          <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.12em] text-[#FFFFFF] transition hover:text-[#FFFFFF] hover:underline">Learn more about practical AI workflows <FiArrowUpRight/></a>
        </footer>
      </main>
    </div>
  );
}
